import * as THREE from 'three';
import { VoxelGrid } from '../core/voxel-grid.js';
import { mulberry32 } from '../core/rng.js';

// ---------------------------------------------------------------------------
// The people waiting for the boat.
//
// A number in the corner would do the job and would be worth nothing: what
// makes you glance across the strait and decide to go is seeing a line of
// people standing on the far quay that was not there an hour ago. So the
// queue is drawn.
//
// One mesh for the whole line, rebuilt when the count changes — which is
// rarely, and never per frame.
// ---------------------------------------------------------------------------

export function registerFerryBlocks(palette) {
    return {
        skin: palette.add('ferrySkin', 0xd8ac8a, { jitter: 0.04 }),
        coatA: palette.add('ferryCoatA', 0x36485e, { jitter: 0.05 }),
        coatB: palette.add('ferryCoatB', 0x6d3f3a, { jitter: 0.05 }),
        coatC: palette.add('ferryCoatC', 0x3f5442, { jitter: 0.05 }),
        cap: palette.add('ferryCap', 0x2a2f36, { jitter: 0.05 }),
        bundle: palette.add('ferryBundle', 0xb08b53, { jitter: 0.06 }),
    };
}

/**
 * A line of people on a quay, facing the water.
 *
 * @param count how many are waiting
 * @param facing the bearing they look along, towards the boat
 */
export function createQueue(palette, material, P, { voxelSize, count, facing, seed = 1 }) {
    if (count <= 0) return null;
    const rng = mulberry32(seed);
    const coats = [P.coatA, P.coatB, P.coatC];

    // Two ranks, so a dozen people are a crowd rather than a procession.
    const perRank = Math.ceil(count / 2);
    const W = perRank * 2 + 2;
    const g = new VoxelGrid(W, 6, 6);

    for (let i = 0; i < count; i++) {
        const rank = i % 2;
        const x = 1 + Math.floor(i / 2) * 2;
        const z = 2 + rank * 2;
        const coat = coats[Math.floor(rng() * coats.length)];

        g.set(x, 0, z, P.cap);          // boots
        g.box(x, 1, z, x, 2, z, coat);  // coat
        g.set(x, 3, z, P.skin);         // head
        if (rng() < 0.55) g.set(x, 4, z, P.cap);        // a cap on some
        if (rng() < 0.3) g.set(x + (rank ? -1 : 1), 1, z, P.bundle);   // luggage
    }

    const mesh = new THREE.Mesh(
        g.build(palette, {
            voxelSize,
            origin: new THREE.Vector3(-(W / 2) * voxelSize, 0, -3 * voxelSize),
        }),
        material,
    );
    mesh.rotation.y = facing;
    mesh.name = 'queue';
    return mesh;
}
